import { Badge, Button, Card, Col, OverlayTrigger, Row, Tooltip } from 'react-bootstrap';
import {
  FaEdit,
  FaFileMedical,
  FaPaperclip,
  FaPrescriptionBottleAlt,
  FaReply,
  FaShare,
  FaUser,
  FaUserMd,
  FaVial,
} from 'react-icons/fa';
import { navigateToProntuario } from 'utils/prontuarioNav';

// Helper 1: Formatador de Hora (ex: "0830" -> "08:30")
function formatHora(hora) {
  if (!hora) return '';
  const h = String(hora).padStart(4, '0');
  return `${h.slice(0, 2)}:${h.slice(2, 4)}`;
}

// Helper 2: Sanitizador / Parser seguro de RTF para Texto
function rtfToParagraphs(content) {
  if (!content) return [];

  // Se não parecer RTF, retorna como linhas normais
  if (typeof content === 'string' && !content.startsWith('{\\rtf')) {
    return content.split(/\r?\n/).filter((s) => s.trim().length > 0);
  }

  try {
    let text = content
      .replace(/\\fonttbl[sS]+?;}/g, '')
      .replace(/\\colortbl[sS]+?;}/g, '')
      .replace(/\\'([0-9a-fA-F]{2})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/\\[a-z]+[0-9]* ?/gi, '')
      .replace(/[{}]/g, '')
      .replace(/Arial;?/gi, '')
      .replace(/[;]+/g, '')
      .replace(/\r/g, '')
      .replace(/\\u([0-9]+)\\?/g, (_, code) => String.fromCharCode(code));

    return text
      .split(/\n|\\par[d]?/i)
      .map((s) => s.trim())
      .filter(Boolean);
  } catch (e) {
    console.error('Erro ao converter RTF:', e);
    return [String(content)];
  }
}

const MensagemDetalhe = ({
  mensagem,
  onResponder,
  onEditar,
  onEncaminhar,
  onHide,
  funcionarios = [],
}) => {
  if (!mensagem) return null;

  const codMensagem = mensagem.CODMENSAGEM;
  const horaFormatada = formatHora(mensagem.HORA);
  const dataExibicao = mensagem.DATA_FORMATADA || '-';

  const textoParagrafos = rtfToParagraphs(mensagem.MENSAGEM);

  const nomePaciente = mensagem.NOMEPAC || 'Nenhum paciente associado';
  const nomeMedico = mensagem.CODINOME || null;

  const tipo = (mensagem.TIPO_PEDIDO || mensagem.tipoPedido || '').toUpperCase();
  const prioridade = (mensagem.PRIORIDADE || 'NORMAL').toUpperCase();
  const status = (mensagem.STATUS || 'PENDENTE').toUpperCase();

  // Render do Botão Principal conforme o Tipo de Mensagem/Pedido
  const renderBotaoAcao = () => {
    if (tipo === 'RECEITA') {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tt-rec">Emitir nova receita no prontuário</Tooltip>}
        >
          <Button
            variant="success"
            size="sm"
            onClick={onResponder}
            className="d-inline-flex align-items-center gap-1 shadow-sm"
          >
            <FaPrescriptionBottleAlt /> Nova Receita
          </Button>
        </OverlayTrigger>
      );
    }

    if (tipo === 'ATESTADO') {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tt-atest">Emitir novo atestado no prontuário</Tooltip>}
        >
          <Button
            variant="primary"
            size="sm"
            onClick={onResponder}
            className="d-inline-flex align-items-center gap-1 shadow-sm"
          >
            <FaFileMedical /> Novo Atestado
          </Button>
        </OverlayTrigger>
      );
    }

    if (['PEDIDO', 'EXAME', 'PEDIDO_EXAME', 'REQUISICAO'].includes(tipo)) {
      return (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip id="tt-req">Criar nova requisição de exame</Tooltip>}
        >
          <Button
            variant="warning"
            size="sm"
            onClick={onResponder}
            className="d-inline-flex align-items-center gap-1 text-white shadow-sm"
          >
            <FaVial /> Nova Requisição
          </Button>
        </OverlayTrigger>
      );
    }

    return (
      <OverlayTrigger
        placement="top"
        overlay={<Tooltip id="tt-resp">Responder diretamente ao remetente</Tooltip>}
      >
        <Button
          variant="secondary"
          size="sm"
          onClick={onResponder}
          className="d-inline-flex align-items-center gap-1 shadow-sm"
        >
          <FaReply /> Responder
        </Button>
      </OverlayTrigger>
    );
  };

  return (
    <Card className="border-0 shadow-sm bg-white rounded-3">
      <Card.Body className="p-3">
        {/* CABEÇALHO */}
        <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
          <div className="d-flex align-items-center gap-2">
            <h6 className="fw-bold text-primary mb-0">Mensagem #{codMensagem}</h6>
            {tipo && (
              <Badge bg="info" className="text-uppercase px-2 py-1">
                {tipo}
              </Badge>
            )}
          </div>
          <div className="d-flex gap-1">
            <Badge
              bg={prioridade === 'ALTA' || prioridade === 'URGENTE' ? 'danger' : 'secondary'}
              className="text-uppercase"
            >
              {prioridade}
            </Badge>
            <Badge
              bg={status === 'CONCLUIDO' ? 'success' : 'warning'}
              className="text-uppercase text-dark"
            >
              {status}
            </Badge>
          </div>
        </div>

        {/* INFORMAÇÕES DE REMETENTE E DESTINO */}
        <Row className="g-2 mb-3 small bg-light p-2 rounded border mx-0">
          <Col xs={12} sm={6}>
            <span className="fw-bold text-secondary">De:</span>{' '}
            <span className="text-dark fw-medium">{mensagem.FO_NOMEFUNC}</span>
          </Col>
          <Col xs={12} sm={6}>
            <span className="fw-bold text-secondary">Para:</span>{' '}
            <span className="text-dark fw-medium">{mensagem.FD_NOMEFUNC}</span>
          </Col>
          <Col xs={12} sm={6}>
            <span className="fw-bold text-secondary">Data / Hora:</span>{' '}
            <span className="text-dark">
              {dataExibicao} às {horaFormatada || '--:--'}
            </span>
          </Col>
          <Col xs={12} sm={6}>
            <span className="fw-bold text-secondary">Lida:</span>{' '}
            <span className="text-dark">{mensagem.LEITURA === 'S' ? 'Sim' : 'Não'}</span>
          </Col>
        </Row>

        {/* PACIENTE E MÉDICO VINCULADOS */}
        {(mensagem.CODPAC || nomeMedico) && (
          <div className="mb-3 p-2 border rounded bg-light-subtle small">
            {mensagem.CODPAC && (
              <div className="d-flex align-items-center gap-2 text-dark mb-1">
                <FaUser className="text-primary" />
                <span className="fw-bold text-secondary">Paciente:</span>
                <span>
                  {nomePaciente} {mensagem.CODPAC ? `(Cód. ${mensagem.CODPAC})` : ''}
                </span>
              </div>
            )}
            {nomeMedico && (
              <div className="d-flex align-items-center gap-2 text-dark">
                <FaUserMd className="text-info" />
                <span className="fw-bold text-secondary">Médico:</span>
                <span>{nomeMedico}</span>
              </div>
            )}
          </div>
        )}

        {/* CONTEÚDO DA MENSAGEM */}
        <div className="my-3">
          <span className="fw-bold text-secondary d-block mb-1 small d-flex align-items-center gap-1">
            <FaPaperclip size={12} /> Conteúdo:
          </span>
          <div
            className="p-3 bg-light rounded border small text-break"
            style={{ minHeight: '100px', maxHeight: '300px', overflowY: 'auto' }}
          >
            {textoParagrafos.length === 0 ? (
              <p className="text-muted italic mb-0">- Sem conteúdo -</p>
            ) : (
              textoParagrafos.map((p, i) => (
                <p key={i} className="mb-2 last-mb-0">
                  {p}
                </p>
              ))
            )}
          </div>
        </div>

        {/* BARRA DE AÇÕES */}
        <div className="d-flex flex-wrap align-items-center gap-2 pt-2 border-top">
          {/* {renderBotaoAcao()} */}

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tt-edit">Editar mensagem existente</Tooltip>}
          >
            <Button
              variant="success"
              size="sm"
              onClick={() => {
                if (navigateToProntuario(mensagem.CODPAC)) {
                  onHide();
                }
              }}
              className="d-inline-flex align-items-center gap-1"
            >
              <FaEdit /> Prontuário
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tt-edit">Editar mensagem existente</Tooltip>}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={onEditar}
              className="d-inline-flex align-items-center gap-1"
            >
              <FaEdit /> Editar
            </Button>
          </OverlayTrigger>

          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tt-enc">Encaminhar conteúdo para outro usuário</Tooltip>}
          >
            <Button
              variant="outline-info"
              size="sm"
              onClick={onEncaminhar}
              className="d-inline-flex align-items-center gap-1"
            >
              <FaShare /> Encaminhar
            </Button>
          </OverlayTrigger>
        </div>
      </Card.Body>
    </Card>
  );
};

export default MensagemDetalhe;
